// src/pages/ProductionDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Spin,
  Table,
  Tooltip,
  message,
  DatePicker,
} from "antd";
import {
  FileTextOutlined,
  FileDoneOutlined,
  ReloadOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  DollarCircleOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

// Small formatter helpers
const toCurrency = (n) => {
  const num = Number(n) || 0;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(num);
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const sameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();
const sameMonth = (a, b) =>
  new Date(a).getMonth() === new Date(b).getMonth() &&
  new Date(a).getFullYear() === new Date(b).getFullYear();

const firstDayOfThisMonth = () => {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), 1);
};
const lastDayOfThisMonth = () => {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth() + 1, 0, 23, 59, 59, 999);
};

const withinRange = (dateValue, start, end) => {
  if (!dateValue) return false;
  const d = new Date(dateValue);
  if (isNaN(d)) return false;
  return d.getTime() >= start.getTime() && d.getTime() <= end.getTime();
};

// ===================== ✅ Special Instructions helpers =====================
const safeJsonParse = (v) => {
  if (v == null) return null;
  if (typeof v === "object") return v;
  if (typeof v !== "string") return null;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};

const resolveUrl = (u) => {
  const url = String(u || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;

  const base = (axiosInstance?.defaults?.baseURL || "").trim();
  if (!base) return url;

  const b = base.replace(/\/$/, "");
  const p = url.startsWith("/") ? url : `/${url}`;
  return `${b}${p}`;
};

const fileNameFromUrl = (url) => {
  try {
    const clean = String(url || "").split("?")[0].split("#")[0];
    const parts = clean.split("/");
    return decodeURIComponent(parts[parts.length - 1] || "file");
  } catch {
    return "file";
  }
};

const isPdfUrl = (url) => /\.pdf(\?|#|$)/i.test(String(url || ""));
const isImageUrl = (url) =>
  /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(String(url || ""));

const normalizeSpecialQuote = (order) => {
  const raw =
    order?.special_quote ||
    order?.specialQuote ||
    order?.specialInstruction ||
    order?.specialInstructions ||
    order?.special_quote_json ||
    // ✅ sometimes backend might send nested order object
    order?.order?.special_quote ||
    null;

  const obj = safeJsonParse(raw);

  const text =
    (obj && (obj.text || obj.note || obj.message)) ||
    (typeof raw === "string" && !obj ? raw : "") ||
    "";

  const filesRaw =
    (obj && (obj.files || obj.attachments || obj.uploads)) ||
    order?.files ||
    order?.attachments ||
    [];

  const filesArr = Array.isArray(filesRaw) ? filesRaw : [];

  const files = filesArr
    .map((f) => {
      if (!f) return null;

      if (typeof f === "string") {
        const url = resolveUrl(f);
        const name = fileNameFromUrl(url);
        return { url, name, isPdf: isPdfUrl(url), isImage: isImageUrl(url) };
      }

      const url =
        resolveUrl(
          f.url || f.fileUrl || f.path || f.file || f.location || f.link || f.src || ""
        ) || "";
      if (!url) return null;

      const name =
        f.name ||
        f.fileName ||
        f.filename ||
        f.originalName ||
        f.original_name ||
        fileNameFromUrl(url);

      const mime = String(f.mimeType || f.mimetype || f.type || "").toLowerCase();
      const isPdf = mime.includes("pdf") || isPdfUrl(url);
      const isImage = mime.startsWith("image/") || isImageUrl(url);

      return { url, name, isPdf, isImage };
    })
    .filter(Boolean);

  const hasAny = Boolean(String(text || "").trim()) || files.length > 0;
  return { text: String(text || ""), files, hasAny };
};
// ==========================================================================

const ProductionDashboard = () => {
  const navigate = useNavigate();
  const [orderSort, setOrderSort] = useState("newest");

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);

  // 📅 Stats filter range
  const [statsRange, setStatsRange] = useState([firstDayOfThisMonth(), new Date()]);

  // Role
  const userRole = String(localStorage.getItem("userRole") || "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const isProductionAdmin = userRole === "production admin";
  const isAdmin = userRole === "admin";
  const isSuperAdmin = userRole === "super admin";
  const isProductionManager = userRole === "production manager";

  // ✅ Only these roles can see invoices
  const canSeeInvoices = isSuperAdmin || isProductionManager;

  // ✅ Admin cannot see costs (everyone else can)
  const canSeeCosts = !isAdmin;

  const fetchProduction = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axiosInstance.get("/admin/production/list", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const list = Array.isArray(data?.orders) ? data.orders : Array.isArray(data) ? data : [];
      setOrders(list);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to load production data.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduction();
  }, []);

  const now = new Date();

  const parseDate = (v) => {
    if (!v) return null;
    const d = new Date(v);
    if (!isNaN(d)) return d;

    const asStr = String(v);
    const m = asStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(`${m[1]}-${m[2]}-${m[3]}T00:00:00`);

    const m2 = asStr.match(/^(\d{2})-(\d{2})-(\d{2})$/);
    if (m2) return new Date(`20${m2[3]}-${m2[2]}-${m2[1]}T00:00:00`);

    return null;
  };

  // KPIs: Today + This Month
  const kpisFixed = useMemo(() => {
    let todays = 0;
    let monthly = 0;

    for (const o of orders) {
      const d = parseDate(o.orderDate);
      if (!d) continue;
      if (sameDay(d, now)) todays += 1;
      if (sameMonth(d, now)) monthly += 1;
    }

    return { todays, monthly };
  }, [orders, now]);

  // KPIs based on range: Total £ in range
  const kpisRange = useMemo(() => {
    const [start, end] = statsRange;
    let totalCostInRange = 0;

    for (const o of orders) {
      if (!withinRange(parseDate(o.orderDate), start, end)) continue;

      const qty = Number(o.quantity) || 0;
      const cost = Number(o.factory_price || o.factoryPrice || 0);

      totalCostInRange += qty * cost;
    }

    return { totalCostInRange };
  }, [orders, statsRange]);

  // Production counts
  const prodCounts = useMemo(() => {
    const tally = { in_progress: 0, complete: 0, shipped: 0 };

    for (const o of orders) {
      const s = String(o.sheetProductionStatus || o.productionStatus || "").toLowerCase();

      if (s === "in progress") tally.in_progress++;
      else if (s === "complete") tally.complete++;
      else if (s === "shipped") tally.shipped++;
    }

    return tally;
  }, [orders]);

  const columns = [
    {
      title: "No",
      key: "srno",
      width: 60,
      render: (_, record) => {
        if (!record.sequence_no || !record.sequence_month) return "-";

        const sameMonthOrders = (orders || [])
          .filter(
            (o) =>
              o &&
              o.sequence_month === record.sequence_month &&
              Number.isFinite(Number(o.sequence_no))
          )
          .slice();

        if (!sameMonthOrders.length) {
          return (
            <span className="font-semibold text-gray-800">
              {String(record.sequence_no)}
            </span>
          );
        }

        sameMonthOrders.sort((a, b) => Number(b.sequence_no) - Number(a.sequence_no));

        const maxSeq =
          Number(sameMonthOrders[0].sequence_no) || Number(record.sequence_no) || 0;

        let indexInMonth = sameMonthOrders.findIndex((o) => o.id === record.id);

        if (indexInMonth === -1 && Number.isFinite(Number(record.sequence_no))) {
          indexInMonth = sameMonthOrders.findIndex(
            (o) => Number(o.sequence_no) === Number(record.sequence_no)
          );
        }

        if (indexInMonth === -1) {
          return (
            <span className="font-semibold text-gray-800">
              {String(record.sequence_no)}
            </span>
          );
        }

        const displayNo = maxSeq - indexInMonth;
        return <span className="font-semibold text-gray-800">{displayNo}</span>;
      },
    },

    {
      title: "Date",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 80,
      render: (date) => {
        const dt = new Date(date);
        const val = isNaN(dt)
          ? "-"
          : dt.toLocaleDateString(undefined, {
              year: "2-digit",
              month: "2-digit",
              day: "2-digit",
            });

        return (
          <div className="text-sm text-gray-600 font-medium bg-gray-50 px-2 py-1 rounded text-center">
            {val}
          </div>
        );
      },
    },

    { title: "Product", dataIndex: "product_name", key: "product_name", width: 120 },
    { title: "Size", dataIndex: "size", key: "size", width: 100 },
    // ============================================================================

    {
      title: "Fabric / Color",
      key: "fabric_color",
      width: 100,
      render: (_, record) => {
        const fabricName = record.fabric_name || "-";
        const color = record.color;

        const colorMap = {
          ivory: "#FFFFF0",
          cream: "#FFFDD0",
          beige: "#F5F5DC",
          coffee: "#6F4E37",
          "new coffee": "#8B6A4E",
          silver: "#C0C0C0",
          grey: "#808080",
          gray: "#808080",
          black: "#000000",
          "duck-egg": "#CFECEC",
          blue: "#1677FF",
          pink: "#FFC0CB",
          white: "#FFFFFF",
        };

        const key = (color || "").toLowerCase().trim();
        const bg = colorMap[key] || "#E5E7EB";

        const isLight = (() => {
          const c = bg.substring(1);
          const rgb = parseInt(c, 16);
          const r = (rgb >> 16) & 0xff;
          const g = (rgb >> 8) & 0xff;
          const b = rgb & 0xff;
          return (r * 299 + g * 587 + b * 114) / 1000 > 150;
        })();

        return (
          <div className="flex flex-col gap-1 items-center">
            <div className="text-xs font-semibold text-gray-700 text-center">{fabricName}</div>

            {color && (
              <div
                className="px-2 py-1 rounded-md border text-xs font-medium text-center shadow-sm"
                style={{
                  backgroundColor: bg,
                  color: isLight ? "#000" : "#fff",
                  minWidth: 70,
                  textTransform: "capitalize",
                }}
              >
                {color}
              </div>
            )}
          </div>
        );
      },
    },

    { title: "Storage", dataIndex: "storageType", key: "storageType", width: 100 },
    { title: "Qty", dataIndex: "quantity", key: "quantity", width: 80 },
     // ===================== ✅ Special Instructions (NO TOOLTIP, NO TRUNCATE) =====================
    {
      title: "Special Instructions",
      key: "special_quote",
      width: 200,
      render: (_, record) => {
        const sp = normalizeSpecialQuote(record);

        if (!sp.hasAny) return <span className="text-gray-400 text-xs italic">None</span>;

        const text = String(sp.text || "").trim();
        const files = Array.isArray(sp.files) ? sp.files : [];

        return (
          <div style={{ maxWidth: 200 }}>
            {text ? (
              <div
                className="text-xs text-gray-800"
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                }}
              >
                {text}
              </div>
            ) : null}

            {files.length > 0 ? (
              <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                {files.map((f, idx) => {
                  const icon = f.isPdf ? "📄" : f.isImage ? "🖼️" : "📎";
                  return (
                    <a
                      key={`${f.url}-${idx}`}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-600 underline"
                      style={{ wordBreak: "break-all" }}
                    >
                      {icon} {f.name || `File ${idx + 1}`}
                    </a>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Prod. Status",
      key: "status",
      width: 140,
      render: (_, r) => {
        if (r && r.isCancelled) {
  return (
    <span className="px-2 py-1 text-xs font-semibold rounded-full border bg-red-600 text-white border-red-700">
      Cancelled
    </span>
  );
}

        const st = String(r.sheetProductionStatus || r.productionStatus || "-").toLowerCase();

        const badge =
          st === "in progress"
            ? "bg-blue-50 text-blue-700 border-blue-200"
            : st === "complete"
            ? "bg-green-50 text-green-700 border-green-200"
            : st === "shipped"
            ? "bg-purple-50 text-purple-700 border-purple-200"
            : "bg-gray-50 text-gray-700 border-gray-200";

        return (
          <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${badge}`}>
            {st}
          </span>
        );
      },
    },
  ];

  // Recent production orders: Top 10
  const recentTop15 = useMemo(() => {
    const sorted = [...orders].sort((a, b) => {
      const da = parseDate(a.orderDate);
      const db = parseDate(b.orderDate);

      if (da && db) {
        return orderSort === "newest" ? db.getTime() - da.getTime() : da.getTime() - db.getTime();
      }

      return orderSort === "newest" ? (b.id || 0) - (a.id || 0) : (a.id || 0) - (b.id || 0);
    });

    return sorted.slice(0, 10);
  }, [orders, orderSort]);
  const recentTop15WithMonthBreaks = useMemo(() => {
  const result = [];
  let prevMonthKey = null;

  for (const order of recentTop15) {
    const d = parseDate(order.orderDate);
    const monthKey = d
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
      : null;

    const monthLabel = d ? d.toLocaleString(undefined, { month: "long" }) : "";

    if (prevMonthKey && monthKey && monthKey !== prevMonthKey) {
      result.push({
        id: `month-break-${monthKey}`,
        __monthBreak: true,
        label: `${monthLabel} End`,
      });
    }

    result.push(order);
    prevMonthKey = monthKey;
  }

  return result;
}, [recentTop15]);

const productionTableColumns = columns.map((col, index) => ({
  ...col,
  onCell: (record) => {
    if (record?.__monthBreak) {
      return index === 0 ? { colSpan: columns.length } : { colSpan: 0 };
    }
    return col.onCell ? col.onCell(record) : {};
  },
  render: col.render
    ? (value, record, rowIndex) => {
        if (record?.__monthBreak) {
          if (index !== 0) return null;
          return <div className="month-end-row">{record.label || "Month End"}</div>;
        }
        return col.render(value, record, rowIndex);
      }
    : (value, record, rowIndex) => {
        if (record?.__monthBreak) {
          if (index !== 0) return null;
          return <div className="month-end-row">{record.label || "Month End"}</div>;
        }
        return value;
      },
}));

  const presets = [
    {
      label: "Today",
      value: [dayjs().startOf("day"), dayjs()],
    },
    {
      label: "This Month",
      value: [dayjs(firstDayOfThisMonth()), dayjs()],
    },
    {
      label: "All",
      value: [dayjs("2000-01-01"), dayjs("2100-12-31")],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-4 lg:mb-0">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Production Dashboard</h1>
                <p className="text-gray-600 text-lg">Quick overview of production, costs, and archives</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchProduction}
                  className="h-12 px-6 text-base rounded-xl border-gray-300"
                >
                  Refresh Data
                </Button>

                <div className="flex flex-col sm:flex-row gap-3">
                  {canSeeInvoices && (
                    <Tooltip title="Open the invoice management page">
                      <Button
                        type="primary"
                        icon={<FileDoneOutlined />}
                        onClick={() => navigate("/invoices")}
                        className="h-12 px-6 text-base rounded-xl bg-green-600 hover:bg-green-700 border-green-600"
                      >
                        Manage Invoices
                      </Button>
                    </Tooltip>
                  )}

                  {!isProductionAdmin && (
                    <Button
                      icon={<FileTextOutlined />}
                      onClick={() => navigate("/order-sheets")}
                      className="h-12 px-6 text-base rounded-xl border-gray-300"
                    >
                      Manage Order Sheets
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* KPIs (HIDE FULLY FOR ADMIN) */}
        {!isAdmin && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            {canSeeCosts && (
              <div className="flex items-center justify-between mb-4 gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <CalendarOutlined className="text-blue-600" />
                  </div>
                  <div className="text-gray-700 font-medium">Stats Range</div>
                </div>

                <RangePicker
                  allowClear={false}
                  value={statsRange ? statsRange.map((d) => dayjs(d)) : undefined}
                  onChange={(vals) => {
                    if (!vals || vals.length !== 2) return;
                    const [s, e] = vals;
                    setStatsRange([s.toDate(), e.toDate()]);
                  }}
                  showTime={false}
                  presets={presets}
                />
              </div>
            )}

            <Row gutter={[24, 24]}>
              {canSeeCosts && (
                <Col xs={24} lg={12}>
                  <Card bordered={false} className="rounded-xl shadow-sm border border-gray-100 h-full">
                    <div className="flex items-center gap-4 mb-1">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <DollarCircleOutlined className="text-2xl text-blue-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-700 text-lg">Total Order Value</div>
                        <div className="text-2xl font-bold text-gray-900">{toCurrency(kpisRange.totalCostInRange)}</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 pl-16">Calculated for selected range</div>
                  </Card>
                </Col>
              )}

              <Col xs={24} lg={12}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Card bordered={false} className="rounded-xl shadow-sm border border-gray-100 h-full text-center">
                      <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <ClockCircleOutlined className="text-lg text-green-600" />
                      </div>
                      <div className="font-medium text-gray-700 mb-1">Today</div>
                      <div className="text-2xl font-bold text-gray-900">{kpisFixed.todays}</div>
                      <div className="text-xs text-gray-500 mt-1">in Production</div>
                    </Card>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Card bordered={false} className="rounded-xl shadow-sm border border-gray-100 h-full text-center">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <CalendarOutlined className="text-lg text-purple-600" />
                      </div>
                      <div className="font-medium text-gray-700 mb-1">This Month</div>
                      <div className="text-2xl font-bold text-gray-900">{kpisFixed.monthly}</div>
                      <div className="text-xs text-gray-500 mt-1">in Production</div>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        )}

        {/* Production Status */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Production Status</h3>

          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} lg={8}>
              <Card
                bordered={false}
                className="rounded-xl border border-gray-200 hover:shadow-md transition-all duration-300 h-full"
                bodyStyle={{ padding: "20px" }}
                onClick={() =>
                  navigate("/production-orders", {
                    state: { filterStatus: "in progress" },
                  })
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <ClockCircleOutlined className="text-2xl text-purple-600" />
                    </div>

                    <div>
                      <div className="font-semibold text-gray-900 text-lg">In Progress</div>
                      <div className="text-sm text-gray-500">In production</div>
                    </div>
                  </div>

                  <div className="text-3xl font-bold text-gray-900">{prodCounts.in_progress}</div>
                </div>

                <div className="pt-4">
                  <Button
                    type="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/production-orders", {
                        state: { filterStatus: "in progress" },
                      });
                    }}
                    className="w-full h-10 rounded-lg bg-gradient-to-r from-purple-500 to-violet-600 border-none"
                    icon={<ArrowRightOutlined />}
                  >
                    View Details
                  </Button>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Card
                bordered={false}
                className="rounded-xl border border-gray-200 hover:shadow-md transition-all duration-300 h-full"
                bodyStyle={{ padding: "20px" }}
                onClick={() =>
                  navigate("/production-orders", {
                    state: { filterStatus: "complete" },
                  })
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <ClockCircleOutlined className="text-2xl text-amber-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-lg">Complete</div>
                      <div className="text-sm text-gray-500">Completed orders</div>
                    </div>
                  </div>

                  <div className="text-3xl font-bold text-gray-900">{prodCounts.complete}</div>
                </div>

                <div className="pt-4">
                  <Button
                    type="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/production-orders", {
                        state: { filterStatus: "complete" },
                      });
                    }}
                    className="w-full h-10 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 border-none"
                    icon={<ArrowRightOutlined />}
                  >
                    View Details
                  </Button>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Card
                bordered={false}
                className="rounded-xl border border-gray-200 hover:shadow-md transition-all duration-300 h-full"
                bodyStyle={{ padding: "20px" }}
                onClick={() =>
                  navigate("/production-orders", {
                    state: { filterStatus: "shipped" },
                  })
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                      <ClockCircleOutlined className="text-2xl text-rose-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-lg">Shipped</div>
                      <div className="text-sm text-gray-500">Shipped orders</div>
                    </div>
                  </div>

                  <div className="text-3xl font-bold text-gray-900">{prodCounts.shipped}</div>
                </div>

                <div className="pt-4">
                  <Button
                    type="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/production-orders", {
                        state: { filterStatus: "shipped" },
                      });
                    }}
                    className="w-full h-10 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 border-none"
                    icon={<ArrowRightOutlined />}
                  >
                    View Details
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        </div>

        {/* RECENT ORDERS TABLE */}
        <Card
          bordered={false}
          className="rounded-2xl shadow-sm border border-gray-200"
          title={
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckCircleOutlined className="text-blue-600" />
              </div>
              <span className="text-xl font-semibold text-gray-900">Recent Production Orders</span>
            </div>
          }
          extra={
            <div className="flex items-center gap-3">
              <select
                value={orderSort}
                onChange={(e) => setOrderSort(e.target.value)}
                className="h-10 px-3 rounded-lg border border-gray-300 text-sm focus:outline-none"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>

              <Button
                onClick={() => navigate("/production-orders")}
                type="primary"
                icon={<ArrowRightOutlined />}
                className="h-10 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 border-blue-600"
              >
                Manage All
              </Button>
            </div>
          }
        >
          <Spin spinning={loading}>
            <Table
              dataSource={recentTop15WithMonthBreaks}
              columns={productionTableColumns}
              rowKey={(r) => r.id}
              pagination={{ pageSize: 15, showSizeChanger: false }}
              scroll={{ x: "max-content" }}
              className="modern-table"
              rowClassName={(record) => {
  try {
    if (record && record.isCancelled) {
      return "cancelled-row";
    }
  } catch {}
  return "";
}}
            />
          </Spin>
        </Card>
      </div>

      <style jsx>{`
        .modern-table .ant-table-thead > tr > th {
          background: #f8fafc;
          color: #374151;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
        }

        .modern-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          transition: all 0.2s ease;
        }
        .modern-table .ant-table-tbody > tr.cancelled-row > td {
  background: #fecaca !important;
}

.modern-table .ant-table-tbody > tr.cancelled-row:hover > td {
  background: #fca5a5 !important;
}

.modern-table .ant-table-tbody > tr.cancelled-row > td:first-child {
  border-left: 4px solid #dc2626;
}

        .modern-table .ant-table-tbody > tr:hover > td {
          background: #f8fafc;
        }
    .month-end-row {
  width: 100%;
  text-align: center;
  color: #dc2626;
  font-weight: 600;
  padding: 6px 12px;
  background: transparent;
  margin: 0 auto;
}

.modern-table .ant-table-tbody > tr > td[colspan] {
  text-align: center !important;
}

        
      `}</style>
    </div>
  );
};

export default ProductionDashboard;